import PropTypes from 'prop-types';
import React from 'react';
import { createSelector } from 'reselect';
import { Button, IconButton } from 'react-toolbox/lib/button';
import { connect } from 'react-redux';
import { Dialog, Checkbox } from 'react-toolbox';
import moment from 'moment';
import inputStyles from 'styles/input.scss';
import formStyles from 'styles/form';
import RTCheckbox from 'styles/checkbox.scss';
import StyledSelect from 'components/StyledSelect/react_select';
import TextArea from 'components/TextArea';
import UserNote from 'components/UserNote';
import RadioButtons from 'components/RadioButtons';
import { cloneDeep, isObject } from 'lodash';
import S from 'modal.scss';
import { navigate, clearValidationErrors, updatePhaseTask } from 'actionCreators';
import PhaseType from 'types/phase';
import TaskType from 'types/task';
import { getPhaseErrors, getPhaseEvents, getPhaseFlags } from '../../../../../../../../../reducers';
import { urls } from 'helpers/url';
import UpdateDueDate from 'components/UpdateDueDate';
import styles from './styles.scss';

const DEFAULT_STATE = {
  loading: false,
  outcomes: [],
  phase: { phone_numbers: [], status: null },
  note: '',
  dueAt: '',
  commMethod: '',
  nonResponsive: false,
  task: {},
  errors: {},
};

const RESULTS = [
  'Reached',
  'Left Message',
  'Texted',
  'Wrong Number',
  'No Answer',
  'Voicemail Not Setup',
  'Voicemail Full',
  'Phone Disconnected',
  'Other',
  'Reached on other line',
].map(value => ({ value, label: value }));

const NON_RESPONSIVE_FLAG_OPTIONS = {
  name: 'Estimator Inquiry',
  suboption: 'Customer Unreachable',
};

const REQUIRED_ERROR_MSG = 'Can\'t be blank.';

class ContactCustomerModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.defaultState();
  }

  componentWillReceiveProps = newProps => (newProps.active && !this.props.active ?
    this.setState(this.defaultState(newProps)) : null);

  getPhaseParams = () => {
    const { dueAt, commMethod, phase: { status, phone_numbers } } = this.state;
    const phase = { phone_numbers_attributes: phone_numbers };
    if (dueAt) phase.due_at = dueAt;
    if (status) phase.status = status;
    if (commMethod) phase.communication_method = commMethod;
    return phase;
  }

  defaultState = (props = this.props) => {
    const attempt = this.props.events.length + 1;
    return {
      ...DEFAULT_STATE,
      phase: {
        id: props.phase.id,
        phone_numbers: cloneDeep(props.phase.phone_numbers),
      },
      dueAt: moment().add(1, 'day').toISOString(),
      commMethod: props.phase.communication_method || 'none',
      outcomes: props.phase.phone_numbers.map(() => ({ result: [] })),
      nonResponsive: props.flags.length > 0,
      task: props.task,
      attempt,
      outcomeNote: '',
    };
  }

  noteRequired = () => {
    const other = this.state.outcomes.find(o => o.result.find(r => r === 'Other'));
    return this.state.nonResponsive || isObject(other);
  }

  validate = () => {
    const { note, outcomes } = this.state;
    const errors = {};
    if (this.noteRequired() && note.trim() === '') errors.note = REQUIRED_ERROR_MSG;
    outcomes.forEach((outcome, i) => {
      if (!outcome.result.length) errors[`result[${i}]`] = REQUIRED_ERROR_MSG;
    });
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return false;
    }
    return true;
  }

  handleAddEvent = () => {
    const { phase: { id }, task: { slug } } = this.props;
    const { outcomeNote, note, attempt, nonResponsive, outcomes, dueAt, commMethod,
      phase: { phone_numbers }, task: { status } } = this.state;
    return this.props.updatePhaseTask(id, slug, {
      event: {
        data: {
          note: `${outcomeNote}\n${note}`,
          attempt,
          non_responsive: nonResponsive,
          outcomes: outcomes.map((outcome, i) => ({
            ...outcome,
            type: 'phone',
            source: phone_numbers[i].number,
            reached: phone_numbers[i].reached,
            voicemail_not_setup: phone_numbers[i].voicemail_not_setup,
            ok_to_text: phone_numbers[i].ok_to_text,
          })),
          due_at: dueAt,
          communication_method: commMethod,
        },
      },
      phase: this.getPhaseParams(),
      task: { status },
    });
  }

  handleScheduleAndRecord = () => {
    if (!this.validate()) return;
    const { phase } = this.props;
    const url = phase.type === 'Assessment' ? urls.phaseAppointments(phase) : urls.phaseLaborProductAccounting(phase);
    const newState = { task: { ...this.state.task, status: 'complete' }, loading: true };
    this.setState(newState, () => this.handleAddEvent()
      .then(() => this.handleClose())
      .then(() => this.props.navigate(url))
      .catch(() => this.setState({ loading: false })));
  }

  handleRecord = () => {
    const newState = { loading: true };
    if (!this.validate()) return;
    this.setState(newState, () => this.handleAddEvent()
      .then(() => this.handleClose())
      .catch(() => this.setState({ loading: false })));
  }

  handleClose = () => {
    this.setState({ loading: false });
    this.props.clearValidationErrors('PHASE');
    this.props.onClose();
  }

  handleNoteChange = e => this.setState({ note: e.target.value });

  hasReachedResult = () => this.state.outcomes.find(o => o.result.find(r => r === 'Reached'))

  handleOutcomeChange = (i, key) => (options) => {
    const { outcomes } = this.state;
    let { nonResponsive } = this.state;
    outcomes[i][key] = options.map(o => o.value);
    if (key === 'result' && this.hasReachedResult()) {
      nonResponsive = false;
    }
    this.setState({ outcomes, nonResponsive, outcomeNote: this.formatOutcomeNote(outcomes) });
  }

  formatOutcomeNote = outcomes => outcomes.reduce((acc, curr, i) => (curr.result.length === 0 ?
    acc : `${acc}\n${this.state.phase.phone_numbers[i].number} - ${curr.result.join(', ')}`),
    `Attempt #${this.state.attempt}`)

  handlePhoneChange = (i, key) => (val) => {
    const { phase, outcomes } = this.state;
    if (key === 'reached' && val && !phase.phone_numbers[i].reached
      && !outcomes[i].result.find(r => r === 'Reached')) {
      outcomes[i].result.push('Reached');
      phase.phone_numbers.forEach((f, index) => {
        if (f !== phase.phone_numbers[i]) outcomes[index].result.push('Reached on other line');
      });
    }
    if (key === 'voicemail_not_setup' && val && !phase.phone_numbers[i].voicemail_not_setup
      && !outcomes[i].result.find(r => r === 'Voicemail Not Setup')) {
      outcomes[i].result.push('Voicemail Not Setup');
    }
    phase.phone_numbers[i][key] = val;
    this.setState({ phase, outcomes });
  }

  handleNonResponsiveChange = val => this.setState({ nonResponsive: val });

  handleDueChange = dueAt => this.setState({ dueAt });

  handleCommMethodChange = commMethod => this.setState({ commMethod })

  render = () => {
    const { active, serverErrors, showNonResponsive, isInstall } = this.props;
    const { phase, outcomes, note, errors, loading, nonResponsive, dueAt,
            attempt, outcomeNote, commMethod } = this.state;
    return (<Dialog
      active={active}
      className={S.modal}
      type="fullscreen"
      onEscKeyDown={this.handleClose}
    >
      <div className={inputStyles.errored}>
        {serverErrors && Object.keys(serverErrors).length > 0 &&
          <span className={inputStyles.error}>This action could not be completed.</span>}
      </div>
      <IconButton
        icon="close"
        className={S.close}
        onClick={this.handleClose}
        tabIndex={-1}
      />
      <h5 className={S.title}>Contact Customer - Attempt #{attempt}</h5>
      {phase.phone_numbers.map((phone, i) => <div
        key={`phone_number_${i}`} // eslint-disable-line react/no-array-index-key
        className={S.clear}
      >
        <h6 className={S.subtitle}>Phone Number {i + 1}</h6>
        <StyledSelect
          className={`grouped ${S.dropdown}`}
          label={phone.number}
          onChange={this.handleOutcomeChange(i, 'result')}
          options={RESULTS}
          multi
          value={outcomes[i].result}
          error={errors[`result[${i}]`]}
        />
        <div className={formStyles.formElementContainer}>
          <Checkbox
            checked={phone.reached}
            label="Reached on this number"
            theme={RTCheckbox}
            className={`${formStyles.checkboxListItem} ${S.checkboxListItem}`}
            onChange={this.handlePhoneChange(i, 'reached')}
          />
          <Checkbox
            checked={phone.voicemail_not_setup}
            label="Voicemail Not Setup"
            theme={RTCheckbox}
            className={`${formStyles.checkboxListItem} ${S.checkboxListItem}`}
            onChange={this.handlePhoneChange(i, 'voicemail_not_setup')}
          />
          <Checkbox
            checked={phone.ok_to_text}
            label="OK To Text"
            theme={RTCheckbox}
            className={`${formStyles.checkboxListItem} ${S.checkboxListItem}`}
            onChange={this.handlePhoneChange(i, 'ok_to_text')}
          />
        </div>
      </div>)}
      <div className={`${formStyles.formElementContainer} ${S.clear}`}>
        <UpdateDueDate
          value={dueAt}
          onChange={this.handleDueChange}
          errors={errors}
          dateLabel="Update Due Date"
          timeLabel="Update Due Date Time"
          required={false}
        />
      </div>
      {isInstall && <RadioButtons
        label="Automatic Communication Method"
        required
        onChange={this.handleCommMethodChange}
        selectedValue={commMethod}
        radioButtons={[
          { label: 'Text', value: 'text' }, { label: 'Email', value: 'email' }, { label: 'None', value: 'none' },
        ]}
      />}
      <div className={`${formStyles.formElementContainer} ${S.clear} ${S.subtitle}`}>
        <span className={styles.label}>OUTCOME NOTES</span>
        <UserNote className={styles.outcomeNote}>{outcomeNote}</UserNote>
      </div>
      <div className={`${formStyles.formElementContainer} ${S.clear}`}>
        <TextArea
          label={`Additional Notes ${this.noteRequired() ? '*' : ''}`}
          value={note}
          errors={[errors.note]}
          onChange={this.handleNoteChange}
        />
      </div>
      {showNonResponsive && attempt > 2 && !this.hasReachedResult() && <Checkbox
        checked={nonResponsive}
        label="Customer is non-responsive"
        theme={RTCheckbox}
        className={`${RTCheckbox.large} ${S.element}`}
        onChange={this.handleNonResponsiveChange}
      />}
      <div className={S.actions}>
        {!nonResponsive && <Button
          label="Continue Scheduling"
          flat inverse ripple
          className={S.smallAction}
          onClick={this.handleScheduleAndRecord}
          disabled={loading}
        />}
        <Button
          label="Record Attempt"
          flat inverse ripple
          className={S.smallAction}
          onClick={this.handleRecord}
          disabled={loading}
        />
      </div>
    </Dialog>);
  }
}

ContactCustomerModal.propTypes = {
  active: PropTypes.bool.isRequired,
  clearValidationErrors: PropTypes.func.isRequired,
  updatePhaseTask: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  phase: PhaseType.isRequired,
  task: TaskType.isRequired,
  events: PropTypes.array.isRequired,
  serverErrors: PropTypes.shape({}),
  showNonResponsive: PropTypes.bool.isRequired,
  isInstall: PropTypes.bool.isRequired,
};

ContactCustomerModal.defaultProps = {
  active: false,
  showNonResponsive: true,
  isInstall: false,
};

const getAttempts = slug => createSelector(
  state => getPhaseEvents(state, slug),
  events => events.filter(e => e.data.attempt),
);

const getNonResponsiveFlags = createSelector(getPhaseFlags, flags =>
  flags.filter(flag => flag.name === NON_RESPONSIVE_FLAG_OPTIONS.name
    && flag.suboption === NON_RESPONSIVE_FLAG_OPTIONS.suboption));

export default connect((state, ownProps) => ({
  serverErrors: getPhaseErrors(state),
  events: getAttempts(ownProps.task.slug)(state),
  flags: getNonResponsiveFlags(state),
}), {
  navigate,
  clearValidationErrors,
  updatePhaseTask,
})(ContactCustomerModal);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/components/tasks/components/ContactCustomerModal/index.jsx